import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  DataTable,
  DataTableAddRow,
  DataTableCellContent,
  DataTableEmpty,
  DataTableToolbar,
  DataTableToolbarActions,
  EditableTitleField,
  EmptyState,
  Icon,
  Menu,
  MenuPopup,
  MenuTrigger,
  Text,
  createColumnHelper,
  useIsMobile,
  type DataTableColumnDef,
  type DataTableGroupType,
} from "@salestrak/ui";

import { DateText } from "#app/components/date-text";
import { ViewSearch } from "#app/components/filters/view-search";
import { FormShareDialog } from "#app/components/forms/form-share-dialog";
import { FormStatusBadge } from "#app/components/forms/form-status-badge";
import { FormActionTile, FormTile, MetaCount } from "#app/components/forms/form-tile";
import {
  discardDraftWithUndo,
  fillBlockedReason,
  publishBlockedReason,
  renderFormMenuItems,
  unpublishWithUndo,
} from "#app/components/forms/form-menu-items";
import { FormsViewModeToggle, useFormsViewMode } from "#app/components/forms/forms-view-mode";
import { useFormPublish } from "#app/components/forms/use-form-publish";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { useFormBuilderDialogActions } from "#app/lib/form-builder-dialog-context";
import { useFormFillDialogActions } from "#app/lib/form-fill-dialog-context";
import { useLocalGroupPages } from "#app/lib/hooks/use-local-group-pages";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { countOf } from "#app/lib/utils/pluralize";
import {
  formVisibility,
  hasSomethingToPublish,
  hasUnpublishedChanges,
  useFormsStore,
  type Form,
  UNTITLED_FORM,
} from "#app/lib/forms";

const PINNED_COLUMN_IDS = ["form"];

const DEFAULT_HIDDEN_COLUMN_IDS = ["used", "frequency", "updated"];

const h = createColumnHelper<Form>();

type FormUsage = {
  used: number;
  perMonth: number;
};

const NO_USAGE: FormUsage = { perMonth: 0, used: 0 };

type FormActions = {
  fillBlocked: string | null;
  onOpen: () => void;
  onFill: () => void;
  onRename: () => void;
  onShare: () => void;
  onDuplicate: () => void;
  onPublish: () => void;
  publishBlocked: string | null;
  onDiscardDraft: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
};

function FormCard({
  form,
  usage,
  renaming,
  actions,
  onRenameCommit,
  onRenameEnd,
}: {
  form: Form;
  usage: FormUsage;
  renaming: boolean;
  actions: FormActions;
  onRenameCommit: (name: string) => void;
  onRenameEnd: () => void;
}) {
  return (
    <FormTile
      badge={<FormStatusBadge form={form} />}
      date={form.updatedAt}
      form={form}
      menu={
        <Menu>
          <MenuTrigger
            render={
              <Button
                aria-label={`Actions for ${form.name}`}
                size="icon-sm"
                variant="outline"
              >
                <Icon name="ellipsis" size="md" />
              </Button>
            }
          />
          <MenuPopup align="end">{renderMenuRows(form, actions)}</MenuPopup>
        </Menu>
      }
      meta={
        <>
          <FormAccessMark form={form} />
          <MetaCount icon="inbox" label={`Used ${usage.used}`} />
          <MetaCount icon="repeat" label={`Frequency ${usage.perMonth}/mo`} />
        </>
      }
      onOpen={actions.onOpen}
      rename={{ editing: renaming, onCommit: onRenameCommit, onEnd: onRenameEnd }}
    />
  );
}

function FormAccessMark({ form }: { form: Form }) {
  if (formVisibility(form) === "public") return <MetaCount icon="globe" label="Public" />;
  const shares = form.sharedWith?.length ?? 0;
  return <MetaCount icon="lock" label={shares > 0 ? `Private · ${shares} shared` : "Private"} />;
}

function renderMenuRows(form: Form, actions: FormActions) {
  return renderFormMenuItems({
    fillBlocked: actions.fillBlocked,
    onDelete: actions.onDelete,
    ...(hasSomethingToPublish(form)
      ? {
          publish: {
            blocked: actions.publishBlocked,
            label: form.status === "live" ? "Publish changes" : "Publish",
            onClick: actions.onPublish,
          },
        }
      : {}),
    ...(hasUnpublishedChanges(form) ? { onDiscardDraft: actions.onDiscardDraft } : {}),
    ...(form.status === "live" ? { onUnpublish: actions.onUnpublish } : {}),
    onDuplicate: actions.onDuplicate,
    onFill: actions.onFill,
    onOpen: actions.onOpen,
    onRename: actions.onRename,
    ...(formVisibility(form) === "private" ? { onShare: actions.onShare } : {}),
  });
}

export function FormsListPanel() {
  const store = useFormsStore();
  const { openFormBuilder } = useFormBuilderDialogActions();
  const { open: openFill } = useFormFillDialogActions();
  const { mode, setMode } = useFormsViewMode("directory");
  const tableView = useTableViewSettings("forms", {
    defaultHiddenColumnIds: DEFAULT_HIDDEN_COLUMN_IDS,
  });
  // A phone builds no forms, so a blank form is not started there.
  const isMobile = useIsMobile();
  const canCreate = !isMobile;
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Form | null>(null);
  // The dialog reads the live row so a share it writes shows in its chips at once.
  const [sharingId, setSharingId] = useState<string | null>(null);
  const { publish, dialog: publishDialog } = useFormPublish(store, { onShare: (form) => setSharingId(form.formId) });

  const forms = useMemo(
    () =>
      [...store.forms].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [store.forms],
  );
  const [searchValue, setSearchValue] = useState("");
  const visibleForms = useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (!needle) return forms;
    return forms.filter(
      (form) =>
        form.name.toLowerCase().includes(needle) ||
        (form.description ?? "").toLowerCase().includes(needle),
    );
  }, [forms, searchValue]);
  const usageByForm = useMemo(
    () => new Map<string, FormUsage>(forms.map((form) => [form.formId, { perMonth: form.monthlyCount, used: form.usedCount }])),
    [forms],
  );

  const openBuilder = useCallback(
    (form: Form) => openFormBuilder(form.formId),
    [openFormBuilder],
  );
  const onCreate = useCallback(async () => {
    const form = await store.createForm(UNTITLED_FORM);
    if (form) openBuilder(form);
  }, [openBuilder, store]);

  const actionsFor = useCallback(
    (form: Form): FormActions => ({
      fillBlocked: fillBlockedReason(form),
      onDelete: () => setDeleting(form),
      onDiscardDraft: () => discardDraftWithUndo(store, form),
      onDuplicate: async () => {
        const copy = await store.duplicateForm(form.formId);
        if (copy.ok) openBuilder(copy.form);
      },
      onFill: () =>
        openFill({ formId: form.formId, formName: form.name, kind: "pickIssue" }),
      onOpen: () => openBuilder(form),
      onPublish: () => publish(form),
      onRename: () => setRenamingId(form.formId),
      onShare: () => setSharingId(form.formId),
      onUnpublish: () => unpublishWithUndo(store, form.formId),
      publishBlocked: publishBlockedReason(form, store.forms),
    }),
    [openBuilder, openFill, publish, store],
  );

  const commitRename = useCallback(
    (form: Form, name: string) => {
      if (name) store.updateForm(form.formId, { name });
    },
    [store],
  );
  const endRename = useCallback(() => setRenamingId(null), []);

  const columns = useMemo<DataTableColumnDef<Form>[]>(
    () => [
      {
        accessorFn: (form) => form.name,
        cell: ({ row }) => {
          const form = row.original;
          const title = form.name || UNTITLED_FORM;
          return (
            <DataTableCellContent>
              {renamingId === form.formId ? (
                // An input claims the press, so typing in it never opens the row.
                <EditableTitleField
                  aria-label={`Rename ${title}`}
                  editing
                  fill
                  flushLeft
                  onCommit={(name) => commitRename(form, name)}
                  onEditingChange={(next) => {
                    if (!next) endRename();
                  }}
                  chrome="none"
                  placeholder={UNTITLED_FORM}
                  value={form.name}
                  weight="medium"
                />
              ) : (
                <Text size={null} truncate weight="medium">
                  {title}
                </Text>
              )}
            </DataTableCellContent>
          );
        },
        enableHiding: false,
        header: "Form",
        id: "form",
        meta: { minWidth: 320 },
      },
      h.accessor({
        accessorKey: "status",
        cell: ({ row }) => (
          <DataTableCellContent>
            <FormStatusBadge form={row.original} showLive />
          </DataTableCellContent>
        ),
        header: "Status",
        id: "status",
        meta: { width: 130 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <Text size={null} truncate variant="muted">
              {row.original.description?.trim() || "—"}
            </Text>
          </DataTableCellContent>
        ),
        header: "Description",
        id: "description",
        meta: { minWidth: 280 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <FormAccessMark form={row.original} />
          </DataTableCellContent>
        ),
        header: "Access",
        id: "access",
        meta: { width: 190 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <Text size={null} variant="muted">
              {(usageByForm.get(row.original.formId) ?? NO_USAGE).used}
            </Text>
          </DataTableCellContent>
        ),
        header: "Used",
        id: "used",
        meta: { width: 110 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <Text size={null} variant="muted">
              {`${(usageByForm.get(row.original.formId) ?? NO_USAGE).perMonth}/mo`}
            </Text>
          </DataTableCellContent>
        ),
        header: "Frequency",
        id: "frequency",
        meta: { width: 120 },
      }),
      h.display({
        cell: ({ row }) => (
          <DataTableCellContent>
            <DateText date={row.original.updatedAt} />
          </DataTableCellContent>
        ),
        header: "Updated",
        id: "updated",
        meta: { width: 160 },
      }),
    ],
    [commitRename, endRename, renamingId, usageByForm],
  );

  const groupState = useLocalGroupPages(visibleForms, "forms");
  const groups = useMemo<DataTableGroupType<Form>[]>(() => {
    const { total, ...groupProps } = groupState;
    return [{ key: "__all__", label: "All Forms", totalCount: total, ...groupProps }];
  }, [groupState]);

  // Stable, as the group memo requires.
  const groupFooter = useCallback(
    () => (canCreate ? <DataTableAddRow label="New form" onClick={onCreate} /> : null),
    [canCreate, onCreate],
  );

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      {store.formsLoading ? null : store.formsFailed ? (
        <Box align="center" fill="flex" justify="center" padding="xl">
          <EmptyState
            actions={
              <Button onClick={store.refetchForms} size="sm" variant="outline">
                <Text size="sm">Try again</Text>
              </Button>
            }
            description="Check your connection and try again."
            icon="clipboard-check"
            media="icon"
            title="Couldn't load forms"
          />
        </Box>
      ) : forms.length === 0 ? (
        <Box align="center" fill="flex" justify="center" padding="xl">
          <EmptyState
            actions={
              canCreate ? (
                <Button onClick={onCreate} size="sm">
                  <Icon name="plus" size="md" />
                  <Text size="sm">New form</Text>
                </Button>
              ) : undefined
            }
            description={
              isMobile
                ? "Checklists, inspections and sign-offs your team fills out on a Project. Build one from a computer or tablet."
                : "Checklists, inspections and sign-offs your team fills out on a Project."
            }
            icon="clipboard-check"
            media="icon"
            title="No forms yet"
          />
        </Box>
      ) : (
        <>
          <DataTableToolbar>
            <DataTableToolbarActions>
              <ViewSearch
                onValueChange={setSearchValue}
                size="xs"
                tooltip="Search forms"
              />
              <FormsViewModeToggle mode={mode} onModeChange={setMode} />
            </DataTableToolbarActions>
          </DataTableToolbar>
          {mode === "table" ? (
            <DataTable
              pinnedColumnIds={PINNED_COLUMN_IDS}
              columns={columns}
              density="regular"
              emptyState={<DataTableEmpty message="No forms match your search" />}
              getRowId={(form) => form.formId}
              groupFooter={groupFooter}
              groups={groups}
              onRowClick={(row) => openBuilder(row.original)}
              paddingX="lg"
              renderRowActions={(form) => renderMenuRows(form, actionsFor(form))}
              stickyHeader
              {...tableView.tableProps}
              // Opting into onColumnVisibilityChange puts the show/hide picker on the actions-column header.
              onColumnVisibilityChange={tableView.settingsProps.onColumnVisibilityChange}
            />
          ) : visibleForms.length === 0 ? (
            <Box align="center" fill="flex" justify="center" padding="xl">
              <EmptyState icon="search" media="icon" size="xs" title="No forms match your search" />
            </Box>
          ) : (
            <Box fill="flex" minHeight={0} overflow="auto">
              <Box
                aria-label="Forms"
                columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
                gap="md"
                paddingBottom="lg"
                paddingX="lg"
                role="list"
              >
                {visibleForms.map((form) => (
                  <Box key={form.formId} role="listitem">
                    <FormCard
                      actions={actionsFor(form)}
                      form={form}
                      onRenameCommit={(name) => commitRename(form, name)}
                      onRenameEnd={endRename}
                      renaming={renamingId === form.formId}
                      usage={usageByForm.get(form.formId) ?? NO_USAGE}
                    />
                  </Box>
                ))}
                {canCreate ? (
                  <Box role="listitem">
                    <FormActionTile label="New form" onClick={onCreate} />
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </>
      )}
      {publishDialog}
      <FormShareDialog
        form={sharingId ? store.formsById.get(sharingId) ?? null : null}
        onChange={(formId, sharedWith) => store.updateForm(formId, { sharedWith })}
        onOpenChange={(open) => (open ? undefined : setSharingId(null))}
      />
      <TrashConfirmDialog
        confirmLabel="Delete form"
        description={
          deleting
            ? `"${deleting.name}" and its ${countOf((usageByForm.get(deleting.formId) ?? NO_USAGE).used, "submission")} are removed for everyone. This cannot be undone.`
            : ""
        }
        onConfirm={async () => {
          if (deleting) store.deleteForm(deleting.formId);
          setDeleting(null);
        }}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        open={deleting !== null}
        title="Delete this form?"
      />
    </Box>
  );
}
